import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Lock } from 'lucide-react';
import { adminAuth } from '../lib/adminAuth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global admin login dialog. Opened by dispatching `tt:open-admin-login`.
 * The event is emitted by the footer's triple-click detector.
 */
export const AdminLoginModal = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onOpen = () => {
      setError('');
      setPassword('');
      setOpen(true);
    };
    window.addEventListener('tt:open-admin-login', onOpen);
    return () => window.removeEventListener('tt:open-admin-login', onOpen);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/api/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      if (data?.access_token) {
        adminAuth.set(data.access_token);
      }
      setOpen(false);
      setEmail('');
      setPassword('');
      navigate('/admin/jobs');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string' ? detail : 'Login failed. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-login-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 md:p-8 relative">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"
          aria-label="Close"
          data-testid="admin-login-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-blue-700 text-white flex items-center justify-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Administrator Login
            </h2>
            <p className="text-xs text-gray-500">Private area — authorised users only</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-1"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full bg-blue-700 hover:bg-blue-800 text-white"
          >
            {busy ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
};
